// Verificador universal de bundles de evidencia — 100% client-side, cero red.
// Espejo del verificador de referencia verify_bundle.py:
//   integridad, consistencia, cadena de chat (v2/v3), firmas Ed25519 por mensaje (v3),
//   firmas ML-DSA-87 COLD/WARM (noble, FIPS-204). El ancla OTS NO se verifica aqui
//   (requiere nodo Bitcoin). Por eso este verificador no emite VALIDA en ningun caso:
//   su techo es CONSISTENTE. La aprobacion la da verify_bundle.py contra un nodo propio.
import JSZip from 'jszip';
import { ml_dsa87 } from '@noble/post-quantum/ml-dsa.js';
import { ed25519 } from '@noble/curves/ed25519.js';
import { sha256Hex } from './api';
import { verifyChainEntries } from './chain';

const b64d = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

// Raiz de confianza de la co-firma soberana: huellas sha256 de las claves publicas
// ML-DSA-87 generadas en la Raspberry Pi 500 aislada. Se pinea en el cliente porque
// signatures.json NO esta cubierto por el ancla OTS: comparar la huella contra la clave
// que viene en el propio bundle es circular (un bundle falso trae su clave y su huella,
// coherentes entre si). Conjunto -> permite rotar la clave sin invalidar el pasado.
// Contrastala fuera de banda (README del bundle, la web, un correo previo).
const TRUSTED_COLD_FINGERPRINTS = new Set([
  '8453a25a41d6fe8fcb5647600f042a7c303daaca79b80928534025711981c6a1',
]);

export async function verifyBundle(file) {
  const report = { checks: [], proof: null, otsBytes: 0, noCold: false, verdict: 'fail' };
  const add = (id, ok, meta = {}) => report.checks.push({ id, ok, ...meta });
  let zip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    add('files', false);
    return report;
  }
  const proofFile = zip.file('proof.json');
  const sigsFile = zip.file('signatures.json');
  if (!proofFile || !sigsFile) { add('files', false); return report; }
  const proofBytes = await proofFile.async('uint8array');
  let proof, sigs;
  try {
    proof = JSON.parse(new TextDecoder().decode(proofBytes));
    sigs = JSON.parse(await sigsFile.async('string'));
  } catch {
    add('files', false);
    return report;
  }
  report.proof = proof;

  // 1. Integridad: sha256(proof.json) == signatures.proof_hash
  const ph = await sha256Hex(proofBytes);
  add('integrity', ph === sigs.proof_hash, { hash: ph });

  // 2. Consistencia de campos cruzados
  const consistent = ['agreement_id', 'content_hash', 'sealed_at']
    .every((f) => proof[f] == null || sigs[f] == null || proof[f] === sigs[f]);
  add('consistency', consistent);

  // 2b. Manifiesto: proof.files = {nombre: sha256}. Ficheros informativos (p.ej. README.md)
  // congelados al sellar: su hash viaja dentro de proof.json y queda anclado en Bitcoin.
  // Cada fichero listado debe existir y coincidir byte a byte. Sin manifiesto: sin comprobacion.
  if (proof.files && typeof proof.files === 'object' && !Array.isArray(proof.files)) {
    let manOk = true;
    const names = [];
    for (const [name, h] of Object.entries(proof.files)) {
      const f = zip.file(name);
      if (!f) { manOk = false; break; }
      const got = await sha256Hex(await f.async('uint8array'));
      if (typeof h !== 'string' || got !== h.toLowerCase()) { manOk = false; break; }
      names.push(name);
    }
    add('manifest', manOk, { files: names });
  }

  // 3. Cadena de chat (v2/v3)
  if (proof.chat_merkle_root) {
    const chainFile = zip.file('chat_chain.json');
    if (!chainFile) {
      add('chain', false, { count: 0 });
    } else {
      try {
        const entries = JSON.parse(await chainFile.async('string')).entries || [];
        const r = await verifyChainEntries(entries, proof.chat_merkle_root);
        add('chain', r.ok, { count: entries.length, index: r.index });
        // 4. Firmas Ed25519 por mensaje (v3)
        if (proof.msg_sigs) {
          const keys = proof.sig_keys || {};
          let okCount = 0, valid = true;
          for (const e of entries) {
            if (!e.sig_b64) continue;
            const pk = keys[e.role];
            const msg = new TextEncoder().encode(`x39msg:v3:${proof.agreement_id}:${e.content_hash}:${e.cts}`);
            let good = false;
            try { good = !!pk && ed25519.verify(b64d(e.sig_b64), msg, b64d(pk)); } catch { good = false; }
            if (good) okCount += 1; else valid = false;
          }
          // Un bundle v3 debe traer TODOS los mensajes firmados: los contadores de proof.json
          // los escribe quien genera el bundle, asi que se contrastan contra la cadena real.
          // Mensajes sin firma en un hilo v3 = fallo (no se dejan pasar "los que falten").
          const total = entries.length;
          const allSigned = proof.v === 'X39-NOTARIA-3' ? okCount === total : true;
          add('msgsigs', valid && allSigned && okCount === proof.msg_sigs.signed && total === proof.msg_sigs.total,
            { signed: okCount, total });
        }
      } catch {
        add('chain', false, { count: 0 });
      }
    }
  }

  // 5. Firmas ML-DSA-87. COLD soberana: cuenta para el veredicto y SOLO acredita si su clave
  // reduce a una huella pineada (fin de la comprobacion circular). WARM: clave de servidor
  // retirada 2026-07-16 (SEC-003), nunca fue soberana -> informativa, no altera el veredicto.
  // Ausencia de COLD = aviso, no fallo: la co-firma se aplica post-sellado y su ausencia no
  // es detectable criptograficamente.
  for (const tier of ['cold', 'warm']) {
    const blk = sigs[tier];
    if (!blk) { if (tier === 'cold') report.noCold = true; continue; }
    let sigOk = false, fp = null;
    try {
      const pk = b64d(blk.public_key_b64);
      sigOk = ml_dsa87.verify(b64d(blk.signature_b64), proofBytes, pk);
      fp = await sha256Hex(pk);
    } catch { sigOk = false; }
    if (tier === 'cold') {
      const trusted = fp != null && TRUSTED_COLD_FINGERPRINTS.has(fp);
      add('mldsa_cold', sigOk && trusted, { fp, trusted });
    } else {
      add('mldsa_warm', sigOk, { fp, informative: true });
    }
  }

  const otsFile = zip.file('proof.json.ots');
  if (otsFile) report.otsBytes = (await otsFile.async('uint8array')).length;
  // El ancla no se puede verificar en el navegador, pero su AUSENCIA si se detecta:
  // un bundle sin proof.json.ots no tiene ancla que verificar despues, en ningun sitio.
  add('ots_present', !!otsFile, { bytes: report.otsBytes, unverified: true });

  // El veredicto lo deciden solo los checks NO informativos (WARM queda fuera).
  // Sin co-firma COLD pineada, lo unico que se ha comprobado es que el bundle es coherente
  // consigo mismo (datos que escribe quien lo genera) y el ancla OTS no se verifica en el
  // navegador: NO hay raiz de confianza -> 'consistent', nunca 'valid'. Un bundle inventado
  // que omita el bloque cold no debe salir en verde.
  const scored = report.checks.filter((c) => !c.informative);
  const allOk = scored.length > 0 && scored.every((c) => c.ok);
  // El ancla Bitcoin NUNCA se verifica aqui: el techo del navegador es 'consistente'.
  // 'valid' se reserva al verificador de referencia ejecutado contra un nodo propio.
  report.verdict = allOk ? (report.noCold ? 'consistent_nocold' : 'consistent') : 'fail';
  return report;
}
